import { useState } from 'react';

const TOUR_PENDING_KEY = 'carousel_tour_pending';

const steps = [
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
        </svg>
      </div>
    ),
    title: 'Welcome to EventsCarousel',
    desc: 'Your family history, preserved for generations. Let\'s take a quick look at how it works.',
  },
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
        </svg>
      </div>
    ),
    title: 'Navigate your memories',
    desc: 'The left panel organises all your events by When, Where, Who, and What. Click any item to filter your archive instantly.',
  },
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </div>
    ),
    title: 'Check what\'s shared with you',
    desc: 'If someone invited you or tagged you in an event, it won\'t show up in "My Events" — click "Shared with Me" at the top of your events page to find it.',
  },
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
      </div>
    ),
    title: 'Capture an event',
    desc: 'Hit "+ Add Event" to record a memory as it happens — add photos, tag who\'s in them, and record voice stories to bring it to life.',
  },
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2">
          <rect x="3" y="3" width="13" height="13" rx="2"/>
          <path d="M21 8v10a2 2 0 0 1-2 2H9"/>
        </svg>
      </div>
    ),
    title: 'Have hundreds of old photos?',
    desc: 'Use Bulk Import to add up to 300 photos at once — we\'ll automatically group them into events by date and location, so you\'re not starting from zero.',
  },
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
    ),
    title: 'Invite your family',
    desc: 'Go to My Circle and add people by email — they\'ll get their own invite to see what they\'re tagged in, add their own photos, and help build the archive together.',
  },
  {
    icon: (
      <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
    ),
    title: 'You\'re all set',
    desc: 'Start adding your first event whenever you\'re ready — there\'s plenty more to discover along the way, at your own pace.',
  },
];

export function OnboardingTour() {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(
    () => localStorage.getItem(TOUR_PENDING_KEY) === 'true'
  );

  if (!visible) return null;

  function dismiss() {
    localStorage.removeItem(TOUR_PENDING_KEY);
    setVisible(false);
  }

  const isLast = step === steps.length - 1;
  const current = steps[step];

  return (
    <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        {/* Skip */}
        <div className="flex justify-end px-6 pt-5">
          <button
            onClick={dismiss}
            className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
          >
            Skip tour
          </button>
        </div>

        {/* Step content */}
        <div className="px-8 pb-2 pt-4 text-center">
          {current.icon}
          <h3 className="mt-5 text-lg font-bold text-slate-900 tracking-tight">{current.title}</h3>
          <p className="mt-2 text-sm text-slate-500 leading-relaxed">{current.desc}</p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mt-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'w-5 bg-indigo-600' : 'w-1.5 bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-6 pt-5">
          {step > 0 && (
            <button
              onClick={() => setStep(s => s - 1)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              Back
            </button>
          )}
          <button
            onClick={isLast ? dismiss : () => setStep(s => s + 1)}
            className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
          >
            {isLast ? 'Get started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
